// ~/components/ToursGalleryOptimized.js
import React, {
	Suspense,
	useMemo,
	useState,
	useCallback,
	useRef,
	useEffect,
} from "react";
import { GatsbyImage } from "gatsby-plugin-image";
import { PhotographIcon } from "@heroicons/react/outline";

export default function ToursGalleryOptimized({ slides = [], max }) {
	// Filter for the new single image field instead of hero/tile
	const images = useMemo(() => {
		const arr = (slides || []).filter(
			(s) => s?.localFile?.childImageSharp?.gatsbyImageData
		);
		return typeof max === "number" ? arr.slice(0, max) : arr;
	}, [slides, max]);

	const [lb, setLb] = useState({ Comp: null, open: false, index: 0 });
	const openLightbox = useCallback(async (startIndex) => {
		const mod = await import("./ToursLightbox");
		setLb({ Comp: mod.default, open: true, index: startIndex });
	}, []);
	const closeLightbox = useCallback(() => {
		setLb((s) => ({ ...s, open: false }));
	}, []);

	if (images.length === 0) return null;

	const [hero, i1, i2, i3, i4] = images;
	const total = images.length;

	// With a single field, always return the same image data
	const getImgData = (imgNode) =>
		imgNode?.localFile?.childImageSharp?.gatsbyImageData;
	const getMobileImgData = (imgNode) =>
		imgNode?.localFile?.childImageSharp?.gatsbyImageData;

	/* ---- Mobile swipe hint state ---- */
	const mobRef = useRef(null);
	const [showHint, setShowHint] = useState(true);
	useEffect(() => {
		const el = mobRef.current;
		if (!el) return;
		const onScroll = () => {
			if (el.scrollLeft > 2) setShowHint(false);
		};
		el.addEventListener("scroll", onScroll, { passive: true });
		return () => el.removeEventListener("scroll", onScroll);
	}, []);

	return (
		<>
			{/* MOBILE: horizontal snap */}
			<div className="md:hidden relative">
				{(() => {
					const PEEK = 0.18;
					const SLIDE_VW = 100 - PEEK * 100; // e.g., 82vw
					return (
						<>
							<div
								ref={mobRef}
								className="overflow-x-auto flex gap-4 px-2"
								style={{
									scrollBehavior: "smooth",
									WebkitOverflowScrolling: "touch",
									overscrollBehaviorX: "contain",
									scrollSnapType: "x mandatory",
									paddingRight: `${PEEK * 100}vw`,
								}}
								aria-label="Tour photos carousel"
								onScroll={() => setShowHint(false)}
							>
								{images.map((img, i) => (
									<button
										key={img.id ?? i}
										onClick={() => openLightbox(i)}
										className="block text-left focus:outline-none focus-visible:ring"
										style={{
											flex: `0 0 ${SLIDE_VW}vw`,
											scrollSnapAlign: "start",
										}}
										aria-label={`Open photo ${i + 1}`}
									>
										<div className="relative w-full overflow-hidden rounded-lg">
											<GatsbyImage
												image={getMobileImgData(img)}
												alt={img.alternativeText || "Tour photo"}
												loading={i === 0 ? "eager" : "lazy"}
												fetchpriority={i === 0 ? "high" : undefined}
											/>
										</div>
									</button>
								))}
							</div>

							{showHint && (
								<>
									<div
										aria-hidden="true"
										className="pointer-events-none absolute inset-y-0 right-0"
										style={{
											width: "88px",
											background:
												"linear-gradient(to left, rgba(255,255,255,0.95), rgba(255,255,255,0.6), rgba(255,255,255,0))",
										}}
									/>
									<div
										aria-hidden="true"
										className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 shadow px-2 py-2 text-gray-700"
										style={{ animation: "pulse 1.5s ease-in-out infinite" }}
									>
										<svg
											viewBox="0 0 24 24"
											width="18"
											height="18"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<polyline points="9 18 15 12 9 6" />
										</svg>
									</div>
								</>
							)}
						</>
					);
				})()}
			</div>

			{/* DESKTOP */}
			<div className="hidden md:block relative">
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
					{/* HERO */}
					{hero && (
						<button
							onClick={() => openLightbox(0)}
							aria-label="Open hero photo"
							className="
                relative overflow-hidden rounded-lg focus:outline-none focus-visible:ring
                md:col-span-2
              "
						>
							<GatsbyImage
								image={getImgData(hero)}
								alt={hero.alternativeText || "Tour photo"}
								className="w-full h-full"
								loading="eager"
								fetchpriority="high"
							/>
						</button>
					)}

					{/* RIGHT SIDE */}
					<div
						className="
              grid gap-4
              md:col-span-2 md:grid-cols-2  
            "
					>
						{/* Tile 1 */}
						{i1 && (
							<button
								onClick={() => openLightbox(1)}
								aria-label="Open photo 2"
								className="relative overflow-hidden rounded-lg focus:outline-none focus-visible:ring"
							>
								<GatsbyImage
									image={getImgData(i1)}
									alt={i1.alternativeText || "Tour photo"}
									loading="lazy"
								/>
							</button>
						)}

						{/* Tile 2 + See all on md/lg ONLY (hide on xl) */}
						{i2 && (
							<div className="relative overflow-hidden rounded-lg">
								<button
									onClick={() => openLightbox(2)}
									aria-label="Open photo 3"
									className="block w-full h-full focus:outline-none focus-visible:ring"
								>
									<GatsbyImage
										image={getImgData(i2)}
										alt={i2.alternativeText || "Tour photo"}
										loading="lazy"
									/>
								</button>

								{total > 0 && (
									<button
										onClick={() => openLightbox(0)}
										aria-label={`See all photos (${total})`}
										className="
                      hidden md:flex lg:hidden
                      absolute bottom-2 right-2
                      items-center gap-1 rounded-full
                      bg-white text-gray-900 shadow-md
                      px-3 py-1.5 text-xs font-medium
                      hover:bg-gray-100 transition
                    "
									>
										<PhotographIcon className="w-4 h-4" aria-hidden="true" />
										<span>See all ({total})</span>
									</button>
								)}
							</div>
						)}

						{/* Tile 3 (xl only) */}
						{i3 && (
							<button
								onClick={() => openLightbox(3)}
								aria-label="Open photo 4"
								className="hidden lg:block relative overflow-hidden rounded-lg focus:outline-none focus-visible:ring"
							>
								<GatsbyImage
									image={getImgData(i3)}
									alt={i3.alternativeText || "Tour photo"}
									loading="lazy"
								/>
							</button>
						)}

						{/* Tile 4 (xl only) */}
						{i4 && (
							<div className="hidden lg:block relative overflow-hidden rounded-lg">
								<button
									onClick={() => openLightbox(4)}
									aria-label="Open photo 5"
									className="block w-full h-full focus:outline-none focus-visible:ring"
								>
									<GatsbyImage
										image={getImgData(i4)}
										alt={i4.alternativeText || "Tour photo"}
										loading="lazy"
									/>
								</button>

								{total > 0 && (
									<button
										onClick={() => openLightbox(0)}
										aria-label={`See all photos (${total})`}
										className="
                      hidden lg:flex
                      absolute bottom-2 right-2
                      items-center gap-1 rounded-full
                      bg-white text-gray-900 shadow-md
                      px-3 py-1.5 text-xs font-medium
                      hover:bg-gray-100 transition
                    "
									>
										<PhotographIcon className="w-4 h-4" aria-hidden="true" />
										<span>See all ({total})</span>
									</button>
								)}
							</div>
						)}
					</div>
				</div>
			</div>

			{/* LIGHTBOX */}
			{lb.open && lb.Comp && (
				<Suspense fallback={null}>
					<lb.Comp images={images} startIndex={lb.index} onClose={closeLightbox} />
				</Suspense>
			)}
		</>
	);
}
